/**
 * Carrier call-forwarding codes for New Zealand and Australia. The Philippines
 * is offered too, with no named carriers: it uses the generic GSM codes in the
 * fallback, which work on most GSM mobile networks.
 *
 * These are the GSM supplementary-service codes every one of these carriers
 * supports on mobile. They are stated exactly as the customer must dial them,
 * with the cancel code beside each one so nobody is ever stuck with calls going
 * somewhere they did not intend.
 *
 * Two honest caveats are built into the UI rather than hidden:
 *   1. Landlines and business PBX lines vary. The carrier's own app path is
 *      given for every carrier, and the "not sure" fallback below explains how
 *      to find the right code for any line.
 *   2. A clock-based after-hours schedule is not a dial code on any of these
 *      networks. "Only when you cannot take it" (no answer plus busy) is the
 *      honest equivalent, and the carrier app path is where a true schedule is
 *      set. We say so rather than implying the codes do something they do not.
 *
 * `{number}` is replaced with the customer's Help Genie number at render time.
 */
import type { ForwardingCarrier, ForwardingCountry } from "@/types/phone/forwarding";

/** The GSM codes, shared by every carrier here. Kept in one place on purpose. */
const GSM = {
  all: {
    label: "Send every call to your Genie",
    pattern: "**21*{number}#",
    cancelPattern: "##21#",
  },
  noAnswer: {
    label: "Send calls you do not answer",
    pattern: "**61*{number}#",
    cancelPattern: "##61#",
  },
  busy: {
    label: "Send calls that arrive while you are on the phone",
    pattern: "**67*{number}#",
    cancelPattern: "##67#",
  },
  unreachable: {
    label: "Send calls when your phone is off or out of coverage",
    pattern: "**62*{number}#",
    cancelPattern: "##62#",
  },
} as const;

export const FORWARDING_CARRIERS: ForwardingCarrier[] = [
  /* ── New Zealand ───────────────────────────────────────────────────────── */
  {
    id: "spark-nz",
    name: "Spark",
    country: "NZ",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "Spark app → My Services → your mobile → Call forwarding",
    note: "On a Spark landline the codes differ. Dial 123 and ask for call forwarding, or use the app path above.",
  },
  {
    id: "one-nz",
    name: "One NZ",
    country: "NZ",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "My One NZ app → Manage → Call settings → Call forwarding",
  },
  {
    id: "2degrees",
    name: "2degrees",
    country: "NZ",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "My 2degrees app → Manage → Call forwarding",
  },

  /* ── Australia ─────────────────────────────────────────────────────────── */
  {
    id: "telstra",
    name: "Telstra",
    country: "AU",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "My Telstra app → Services → your mobile → Call forwarding",
    note: "On a Telstra fixed line, use *21*{number}# with a single star.",
  },
  {
    id: "optus",
    name: "Optus",
    country: "AU",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "My Optus app → Manage service → Call forwarding",
  },
  {
    id: "vodafone-au",
    name: "Vodafone",
    country: "AU",
    codes: {
      everything: [GSM.all],
      unanswered: [GSM.noAnswer, GSM.busy, GSM.unreachable],
    },
    appPath: "My Vodafone app → Services → Call forwarding",
  },
];

/**
 * The "other carrier / not sure" fallback. Never a dead end: it tells the
 * customer exactly how to find the right code for whatever line they have.
 */
export const FORWARDING_FALLBACK = {
  id: "other",
  name: "Another carrier, or not sure",
  /** Try these first — they work on most GSM mobile networks, including every country offered. */
  genericCodes: [GSM.all, GSM.noAnswer, GSM.busy],
  howToFind: [
    "Open your phone's dialler and try the code above. If it works, your phone shows a confirmation straight away.",
    "If it does not, search your carrier's help site for \"call forwarding\" plus your plan type — mobile and landline use different codes.",
    "Every carrier can also set it for you. Call their support line, say you want calls forwarded to a number you will read out, and give them the Help Genie number above.",
    "On a business phone system, forwarding is set by whoever administers the system, not by the carrier.",
  ],
} as const;

/** Countries offered in the forwarding flow, in display order, with an example number for the test-call input. */
export const FORWARDING_COUNTRIES: { code: ForwardingCountry; name: string; placeholder: string }[] = [
  { code: "NZ", name: "New Zealand", placeholder: "+64 9 123 4567" },
  { code: "AU", name: "Australia", placeholder: "+61 2 1234 5678" },
  { code: "PH", name: "Philippines", placeholder: "+63 917 123 4567" },
];

/** Carriers for one country, in the order we want them shown. */
export const carriersForCountry = (country: ForwardingCountry): ForwardingCarrier[] =>
  FORWARDING_CARRIERS.filter((c) => c.country === country);

/** Substitute the real number into a dial-code pattern. */
export const renderCode = (pattern: string, phoneNumber: string): string =>
  pattern.replace("{number}", phoneNumber.replace(/\s+/g, ""));
